import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import EmailAddressInput from '../form-inputs/email-address-input/EmailAddresInput';
import styles from './BookACallModal.module.scss';
import { NWClient } from '../../client/NWClient';
import { User } from '../../models/user.model';
import { Company } from '../../models/company.model';
import { toast } from 'react-toastify';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectBusinessProfileDetails } from '../../slices/business-profile/businessProfileSlice';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';

interface BookACallModalProps {
  open: boolean;
  onClose: () => void;
  company?: Company | null;
  document_contract?: number | null;
  document_upload?: number | null;
  user: User;
}

export default function BookACallModal({
  open,
  onClose,
  company,
  user,
  document_contract,
  document_upload,
}: BookACallModalProps) {
  const { t } = useTranslation();

  const formRef = useRef<HTMLDivElement | null>(null);

  const businessProfileDetails = useAppSelector(selectBusinessProfileDetails);

  const [formData, setFormData] = useState({
    name: user.first_name + ' ' + user.last_name,
    email: user.email,
    phone: businessProfileDetails.business_phone_number || '',
    business_name: company?.name || '',
    description: '',
  });

  const [isReview, setIsReview] = useState(false);

  useEffect(() => {
    if (document_upload || document_contract) {
      setIsReview(true);
    }
  }, [document_upload, document_contract]);

  const [validationErrors, setValidationErrors] = useState({
    name: false,
    email: false,
    phone: false,
    business_name: false,
    description: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange =
    <K extends keyof typeof formData>(field: K) =>
    (value: (typeof formData)[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCreate = () => {
    setIsSubmitting(true);
    const errors = {
      name: !formData.name.trim(),
      business_name: !formData.business_name.trim(),
      phone: !formData.phone.trim(),
      email: !formData.email.trim(),
      description: !formData.description.trim(),
    };

    setValidationErrors(errors);

    if (Object.values(errors).some((error) => error)) {
      console.error('Required fields are empty');
      return;
    }

    const submissionFormData = new FormData();

    // Append formData fields
    Object.entries(formData).forEach(([key, value]) => {
      submissionFormData.append(key, value);
    });

    // Conditionally append document_contract or document_upload
    if (document_contract) {
      submissionFormData.append('document_contract', document_contract.toString());
    } else if (document_upload) {
      submissionFormData.append('document_upload', document_upload.toString());
    }

    const endpoint = isReview ? 'create-case/' : 'create-lead/';

    NWClient.post('salesforce', submissionFormData, true, endpoint)
      .then(() => {
        isReview
          ? toast.success(t('messages.documentSentForReview'))
          : toast.success(t('messages.callBooked'));
      })
      .catch((error) => {
        console.error('Error submitting form:', error);
        if (error.response.data.message.includes('DUPLICATES_DETECTED')) {
          toast.error(t('messages.demoBookingDuplication'));
        } else {
          toast.error(t('messages.errorOccurred'));
        }
      })
      .finally(() => {
        setIsSubmitting(false);
        onClose();
      });
  };

  return (
    <Modal open={open} onClose={onClose} className={'action-modal'} closeAfterTransition>
      <Grid container className={styles.modal_container}>
        {isSubmitting &&
          Object.keys(validationErrors).every(
            (el) =>
              validationErrors[
                el as 'name' | 'business_name' | 'description' | 'phone' | 'email'
              ] === false
          ) && <ProgressOverlay />}
        <Grid size={{ xs: 6 }}>
          {' '}
          <Card ref={formRef}>
            <CardContent sx={{ paddingBottom: '16px !important' }}>
              <Typography variant='h6' sx={{ marginBottom: '16px' }}>
                {isReview ? t('labels.sendDocumentForReview') : t('labels.bookACall')}
              </Typography>

              <Grid container spacing={2} direction='row'>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='name-input'
                    error={isSubmitting && formData.name === ''}
                    label={`${t('labels.fullName')} *`}
                    variant='outlined'
                    type='text'
                    fullWidth
                    value={formData.name ?? ''}
                    onChange={(e) => handleChange('name')(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='business-name-input'
                    error={isSubmitting && formData.business_name === ''}
                    label={`${t('formInputs.businessNameInputLabel')} *`}
                    variant='outlined'
                    type='text'
                    fullWidth
                    value={formData.business_name ?? ''}
                    onChange={(e) => handleChange('business_name')(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  {' '}
                  <EmailAddressInput
                    value={formData.email}
                    onChange={handleChange('email')}
                    disabled={false}
                    submitting={isSubmitting}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='phone-input'
                    error={isSubmitting && formData.phone === ''}
                    label={`${t('labels.phone')} *`}
                    variant='outlined'
                    type='text'
                    fullWidth
                    value={formData.phone ?? ''}
                    onChange={(e) => handleChange('phone')(e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <textarea
                    id='description-input'
                    className={`${isSubmitting && formData.description === '' ? 'error' : ''}`}
                    placeholder={`${t('labels.description')} *`}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '14px',
                      border: `1px solid ${
                        isSubmitting && formData.description === '' ? '#d32f2f' : '#c4c4c4'
                      }`,
                      borderRadius: '4px',
                      fontFamily: "'Regular Inter', sans-serif",
                      fontSize: '16px',
                      resize: 'vertical',
                      outline: 'none',
                      color: isSubmitting && formData.description === '' ? '#d32f2f' : 'inherit',
                    }}
                    value={formData.description ?? ''}
                    onChange={(e) => handleChange('description')(e.target.value)}
                  />
                  <style>
                    {`
                      #description-input::placeholder {
                        color: ${isSubmitting && formData.description === '' ? '#d32f2f' : '#999'};
                      }
                    `}
                  </style>
                </Grid>
              </Grid>

              <Stack direction='row' gap={'8px'} justifyContent='flex-end' marginTop={'16px'}>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic btn-cancel'
                  onClick={onClose}
                >
                  {isReview ? t('buttons.back') : t('buttons.close')}
                </Button>
                <Button
                  variant='contained'
                  className='btn-default btn-action-basic'
                  onClick={handleCreate}
                >
                  {isReview ? t('buttons.send') : t('buttons.book')}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Modal>
  );
}
